import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ErroApi } from '../../api';
import { Carregando, Erro, Selo, Tabela, Vazio, quando } from '../../componentes/ui';
import type { VisaoGeral as Dados } from '../../tipos';

export function VisaoGeral() {
  const nav = useNavigate();
  const [dados, setDados] = useState<Dados | null>(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.visaoGeral().then(setDados).catch((e) => {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o painel.');
    });
  }, []);

  if (erro) return <div className="page"><Erro>{erro}</Erro></div>;
  if (!dados) return <Carregando />;

  const { contagens, ingestao7d, instalacoes, porSistema, escritoriosSemEmpresa } = dados;
  const totalEscritoriosComSistema = porSistema.reduce((s, p) => s + p.escritorios, 0);
  // Um agente que já falou e parou é o único estado que pede alguém agora.
  // Instalação que nunca falou é a que ainda não foi ao cliente.
  const calados = instalacoes.filter((i) => i.estado === 'calado');

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Visão geral</h1>
          <p>O estado da operação: quem está cadastrado, o que entrou e quais agentes estão calados.</p>
        </div>
      </header>

      <div className="stat-grid">
        <Cartao rotulo="Escritórios" valor={contagens.escritorios} aoClicar={() => nav('/painel/escritorios')} />
        <Cartao rotulo="Empresas" valor={contagens.empresas} aoClicar={() => nav('/painel/empresas')} />
        <Cartao rotulo="Instalações" valor={contagens.instalacoes} aoClicar={() => nav('/painel/instalacoes')} />
        <Cartao rotulo="Pessoas com acesso" valor={contagens.usuarios} aoClicar={() => nav('/painel/pessoas')} />
      </div>

      <section className="bloco">
        <h2>Últimos 7 dias</h2>
        <div className="stat-grid">
          <Cartao rotulo="Lotes recebidos" valor={ingestao7d.lotes} />
          <Cartao rotulo="Documentos aceitos" valor={ingestao7d.aceitos} />
          <Cartao rotulo="Duplicados" valor={ingestao7d.duplicados} discreto />
          <Cartao
            rotulo="Rejeitados"
            valor={ingestao7d.rejeitados}
            alerta={ingestao7d.rejeitados > 0}
          />
        </div>
        {ingestao7d.rejeitados > 0 && (
          <p className="nota">
            Item rejeitado quase sempre é cadastro faltando ou XML que não bate com o CNPJ enviado.
            O motivo de cada um está na resposta do lote, no log do agente.
          </p>
        )}
        {dados.divergenciasAbertas > 0 && (
          <p className="nota">
            <strong>{dados.divergenciasAbertas}</strong> divergência(s) em aberto — numeração quebrada,
            documento sem XML ou evento órfão. A tela de fechamento ainda não existe; por enquanto
            elas ficam registradas no banco.
          </p>
        )}
      </section>

      {(porSistema.length > 0 || escritoriosSemEmpresa > 0) && (
        <section className="bloco">
          <h2>Escritórios por sistema</h2>
          <p className="nota">
            Quantos escritórios têm empresa em cada sistema emissor. Um escritório pode aparecer em
            mais de um, se tiver empresas em sistemas diferentes.
          </p>
          <div className="stat-grid">
            {porSistema.map((s) => (
              <Cartao
                key={s.id}
                rotulo={`${s.nome} · ${s.empresas} ${s.empresas === 1 ? 'empresa' : 'empresas'}`}
                valor={s.escritorios}
                aoClicar={() => nav(`/painel/escritorios?sistema=${s.id}`)}
              />
            ))}
            {escritoriosSemEmpresa > 0 && (
              <Cartao
                rotulo="Sem empresa em nenhum sistema ainda"
                valor={escritoriosSemEmpresa}
                alerta
                aoClicar={() => nav('/painel/escritorios?semSistema=1')}
              />
            )}
          </div>
          {totalEscritoriosComSistema === 0 && escritoriosSemEmpresa === 0 && (
            <p className="nota">Nenhum escritório cadastrado ainda.</p>
          )}
        </section>
      )}

      <section className="bloco">
        <h2>Agentes em campo</h2>
        {instalacoes.length === 0 ? (
          <Vazio
            titulo="Nenhuma instalação ativa"
            descricao="Instalação é a credencial que o agente usa para enviar. Crie uma para cada máquina de cliente."
            acao={<button className="btn btn-primary btn-sm" onClick={() => nav('/painel/instalacoes')}>Ver instalações</button>}
          />
        ) : (
          <>
            {calados.length > 0 && (
              <p className="nota nota-atencao">
                {calados.length === 1
                  ? `“${calados[0].nome}” não fala com o portal há mais de 24 horas.`
                  : `${calados.length} agentes não falam com o portal há mais de 24 horas.`}
                {' '}Pode ser máquina desligada, serviço parado ou internet — vale checar antes que o mês feche.
              </p>
            )}
            <Tabela
              itens={instalacoes}
              chaveDe={(i) => i.id}
              colunas={[
                { rotulo: 'Instalação', principal: true, celula: (i) => <strong>{i.nome}</strong> },
                { rotulo: 'Escritório', fraco: true, celula: (i) => i.escritorio || '—' },
                { rotulo: 'Último contato', fraco: true, celula: (i) => quando(i.ultimoContatoEm) },
                { rotulo: 'Estado', celula: (i) => <Selo tipo={i.estado} /> },
              ]}
            />
          </>
        )}
      </section>
    </div>
  );
}

function Cartao({
  rotulo, valor, aoClicar, alerta, discreto,
}: {
  rotulo: string; valor: number; aoClicar?: () => void; alerta?: boolean; discreto?: boolean;
}) {
  const conteudo = (
    <>
      <span className="stat-valor" style={alerta ? { color: 'var(--status-rejeitado-fg)' } : undefined}>
        {valor.toLocaleString('pt-BR')}
      </span>
      <span className="stat-rotulo">{rotulo}</span>
    </>
  );
  if (!aoClicar) return <div className={`stat ${discreto ? 'stat-discreto' : ''}`}>{conteudo}</div>;
  return <button className="stat stat-clicavel" onClick={aoClicar}>{conteudo}</button>;
}
